import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";

/**
 * Floating section nav (right rail).
 * - IntersectionObserver tracks active section.
 * - Hidden below `lg` breakpoint.
 * - Each dot lives in a fixed-size slot so the dot centers never shift.
 */
const SECTIONS = [
  { id: "top", label: "Home" },
  { id: "what", label: "What we do" },
  { id: "shippers", label: "Why shippers" },
  { id: "process", label: "Process" },
  { id: "carriers", label: "Shippers & Carriers" },
  { id: "compliance", label: "Compliance" },
  { id: "testimonials", label: "Stories" },
  { id: "quote", label: "Get a quote" },
];

export default function SectionNav() {
  const [active, setActive] = useState("top");
  const [hovered, setHovered] = useState(null);
  const observersRef = useRef([]);

  useEffect(() => {
    const ratios = new Map();
    SECTIONS.forEach((s) => ratios.set(s.id, 0));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          ratios.set(e.target.id, e.intersectionRatio);
        });
        let topId = "top";
        let topRatio = -1;
        ratios.forEach((r, id) => {
          if (r > topRatio) {
            topRatio = r;
            topId = id;
          }
        });
        if (topRatio > 0) setActive(topId);
      },
      {
        rootMargin: "-30% 0px -50% 0px",
        threshold: [0, 0.05, 0.25, 0.5, 0.75, 1],
      }
    );

    const found = [];
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) {
        observer.observe(el);
        found.push(el);
      }
    });
    observersRef.current = [observer, found];

    return () => observer.disconnect();
  }, []);

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setActive(id);
  };

  return (
    <div
      className="hidden lg:block fixed right-5 xl:right-7 top-1/2 -translate-y-1/2 z-40"
    >
      <motion.nav
        aria-label="Page sections"
        data-testid="section-nav"
        initial={{ opacity: 0, x: 12 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="relative flex flex-col items-center gap-1 px-2.5 py-3 rounded-full bg-white/55 backdrop-blur-md border border-white/60 shadow-[0_8px_30px_rgba(0,32,80,0.08)]"
      >
        {SECTIONS.map((s) => {
          const isActive = active === s.id;
          const isHovered = hovered === s.id;
          return (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={(e) => handleClick(e, s.id)}
              onMouseEnter={() => setHovered(s.id)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setHovered(s.id)}
              onBlur={() => setHovered(null)}
              aria-label={s.label}
              aria-current={isActive ? "true" : undefined}
              data-testid={`section-nav-dot-${s.id}`}
              className="group relative flex items-center justify-center h-6 w-6"
            >
              {/* Tooltip */}
              <span
                className={`pointer-events-none absolute right-full mr-3 whitespace-nowrap rounded-md bg-[#002050] text-white text-[11px] font-semibold tracking-wide px-2.5 py-1.5 shadow-[0_8px_24px_rgba(0,32,80,0.25)] transition-all duration-200 ${
                  isHovered
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 translate-x-1"
                }`}
              >
                {s.label}
                <span className="absolute right-[-3px] top-1/2 -translate-y-1/2 h-2 w-2 rotate-45 bg-[#002050]" />
              </span>

              {/* Dot — fixed slot, color/scale the inner pellet */}
              <span
                className={`block rounded-full transition-all duration-300 ease-out ${
                  isActive
                    ? "h-2.5 w-2.5 bg-[#0070C0] scale-100"
                    : isHovered
                    ? "h-2 w-2 bg-[#002050] scale-110"
                    : "h-1.5 w-1.5 bg-[#1F2937]/35 scale-100"
                }`}
              />

              {/* Active ring — animates between dots via layoutId */}
              {isActive && (
                <motion.span
                  layoutId="section-nav-ring"
                  className="pointer-events-none absolute h-5 w-5 rounded-full border border-[#0070C0]/50"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
            </a>
          );
        })}
      </motion.nav>
    </div>
  );
}
